import { Calendar } from '@withjoy/joykit/icons';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ScheduleSection } from '../ScheduleSection';
import { SegmentedControl } from '../SegmentedControl/SegmentedControl';
import { FieldMetadata } from '@shared/components/ScheduleEditor/hooks/useScheduleFormState';
import type { ScheduleEditorData } from '@shared/components/ScheduleEditor/ScheduleEditor.types';
import { FormDatePicker } from '../FormDatePicker';
import { DateSelection, Flex } from '@withjoy/joykit';
import { FormTimeZonePicker } from '../FormTimeZonePicker';
import { useTranslation } from '@shared/core/i18n';
import { format, isSameDay } from 'date-fns';

// Mapping of which components are visible for each selected index
const COMPONENT_VISIBILITY = {
  ['startDate']: [0, 1],
  ['endDate']: [1],
  ['timezone']: [0, 1],
};

const isVisible = (component: keyof typeof COMPONENT_VISIBILITY, selectedIndex: number) => {
  return COMPONENT_VISIBILITY[component].includes(selectedIndex);
};

const getDefaultSelectedIndex = (state: ScheduleEditorData): number => {
  if (!state.startTime) return 2; // TBA
  return state.endTime ? 1 : 0; // Start + End or Start Only
};

type TimeDateSectionProps = {
  state: ScheduleEditorData;
  setValue: (key: keyof ScheduleEditorData, value: string | DateSelection) => void;
  meta: Record<keyof ScheduleEditorData, FieldMetadata>;
  timezoneManuallyChangedRef: React.MutableRefObject<boolean>;
};

export const TimeDateSection = (props: TimeDateSectionProps) => {
  const { state, setValue, meta, timezoneManuallyChangedRef } = props;

  // Save initial start and end times
  const initialStartTime = useRef(state.startTime);
  const initialEndTime = useRef(state.endTime);

  const [selectedIndex, setSelectedIndex] = useState(getDefaultSelectedIndex(state));

  const { t, tq } = useTranslation('scheduleEditor');
  const trans = t('form');

  // Use ref to access latest state without causing re-creation of callback
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Set value wrapper to prevent start and end time from being set to invalid values
  const smartSetValue = useCallback(
    (fieldName: 'startTime' | 'endTime', value: DateSelection) => {
      const currentState = stateRef.current;
      const currentEnd = currentState.endTime;
      const currentStart = currentState.startTime;

      // Handle date adjustments to preserve duration
      if (fieldName === 'startTime' && value && currentEnd && currentStart) {
        const duration = currentEnd.getTime() - currentStart.getTime();
        const newStart = value;

        // If new start is after current end, adjust end to preserve duration
        if (newStart > currentEnd) {
          const newEnd = new Date(newStart.getTime() + duration);
          setValue('startTime', value);
          setValue('endTime', newEnd);
          return;
        }
      } else if (fieldName === 'endTime' && value && currentStart && currentEnd) {
        const newEnd = value;

        // If new end is before current start, adjust start to preserve duration
        if (newEnd < currentStart) {
          const duration = currentEnd.getTime() - currentStart.getTime();
          const newStart = new Date(newEnd.getTime() - duration);
          setValue('endTime', value);
          setValue('startTime', newStart);
          return;
        }
      }

      setValue(fieldName, value);
    },
    [setValue],
  );

  // Calculate minimum time for end date picker when on same day as start date
  const endDateMinTime = useMemo(() => {
    if (state.startTime && state.endTime && isSameDay(state.startTime, state.endTime)) {
      return format(state.startTime, 'HH:mm');
    }
    return undefined;
  }, [state.startTime, state.endTime]);

  return (
    <ScheduleSection
      icon={<Calendar size={24} />}
      title={tq`Date and Time`}
    >
      <Flex
        gap="12px"
        flexDirection="column"
      >
        <SegmentedControl
          onSelect={(index, _content) => {
            setSelectedIndex(index);

            // Restore or clear startTime
            const restoredStartTime = initialStartTime.current ?? new Date();
            if (!isVisible('startDate', index)) {
              setValue('startTime', null);
            } else {
              setValue('startTime', restoredStartTime);
            }

            // Restore or clear endTime
            if (!isVisible('endDate', index)) {
              setValue('endTime', null);
            } else {
              // Use initial end time if saved, otherwise use the same time as the restored start time
              setValue('endTime', initialEndTime.current ?? restoredStartTime);
            }
          }}
          selectedIndex={selectedIndex}
          segments={[
            {
              label: tq`Start Only`,
            },
            {
              label: tq`Start + End`,
            },
            {
              label: tq`TBA`,
            },
          ]}
        ></SegmentedControl>
        <Flex
          gap="24px"
          flexDirection="column"
        >
          {isVisible('startDate', selectedIndex) && (
            <FormDatePicker
              fieldName={'startTime'}
              state={state}
              setValue={smartSetValue}
              label={trans.startDateTitle()}
              meta={meta}
            />
          )}
          {isVisible('endDate', selectedIndex) && (
            <FormDatePicker
              fieldName={'endTime'}
              state={state}
              setValue={smartSetValue}
              label={trans.endDateTitle()}
              meta={meta}
              minTime={endDateMinTime}
            />
          )}
          {isVisible('timezone', selectedIndex) && (
            <FormTimeZonePicker
              fieldName={'timezone'}
              state={state}
              setValue={setValue}
              label={trans.timezoneFieldTitle()}
              placeholder={trans.timezoneFieldPlaceholder()}
              meta={meta}
              timezoneManuallyChangedRef={timezoneManuallyChangedRef}
            />
          )}
        </Flex>
      </Flex>
    </ScheduleSection>
  );
};
